import React from "react";
import {
  Table,
  Spinner,
  SpaceBetween,
  Header,
  Alert,
  Grid,
  Button,
} from "@amzn/awsui-components-console";
import "./payload-display.css";
import { configureDomain } from "src/auth/config";
import Logo from "../../resources/clipboard.png";
import Collapsible from "src/pages/commons/collapsible";
interface PayloadProps {
  searchResults: any;
  searchId: string;
}

type dataColumns = {
  [key: string]: string | { label: string; cell: (item: any) => any };
};

const TRANSACTION_COLUMNS: dataColumns = {
  transactionVersion: "transaction version",
  exchangeRate: {
    label: "exchange rate",
    cell: (item: any) => item["fxRateQuote"]["exchangeRate"],
  },
  billingCurrencyCode: "billing currency code",
  localCurrencyCode: "local currency code",
  eventTimeStamp: {
    label: "event timestamp",
    cell: (item: any) => new Date(item["eventTimeStamp"]).toISOString(),
  },
  creationDate: {
    label: "creation date",
    cell: (item: any) => new Date(item["creationDate"]).toISOString(),
  },
  resellerId: "reseller id",
  eventType: "event type",
  transactionType: "transaction type",
  isTest: "is test",
  amortizationTreatment: "amortization treatment",
  intent: "intent",
  intentAction: "intent action",
};

const BALANCE_DATA_COLUMNS: dataColumns = {
  lineNumber: "line number",
  localAmount: "local amount",
  billingAmount: "billing amount",
  type: "type",
  fuseEventAggregationId: "fuse event aggregation id",
};

const CONTROL_PARAMETER_COLUMNS: dataColumns = {
  term: {
    label: "term",
    cell: (item: any) => item["accounting"]["term"] ?? "-",
  },
  product: {
    label: "product",
    cell: (item: any) => item["accounting"]["product"] ?? "-",
  },
  glLineDescriptionPartnerName: {
    label: "gl line description partner name",
    cell: (item: any) =>
      item["accounting"]["glLineDescriptionPartnerName"] ?? "-",
  },
  fuseClientId: {
    label: "fuse client id",
    cell: (item: any) => item["accounting"]["fuseClientId"] ?? "-",
  },
  businessCountryCode: {
    label: "business country code",
    cell: (item: any) => item["accounting"]["businessCountryCode"] ?? "-",
  },
  buyingCustomerCountryCode: {
    label: "buying customer country code",
    cell: (item: any) => item["accounting"]["buyingCustomerCountryCode"] ?? "-",
  },
  refundType: {
    label: "refund type",
    cell: (item: any) => item["accounting"]["refundType"] ?? "-",
  },
  terminationStrategy: {
    label: "termination strategy",
    cell: (item: any) => item["accounting"]["terminationStrategy"] ?? "-",
  },
  accountingTemplate: {
    label: "accounting template",
    cell: (item: any) => item["accounting"]["accountingTemplate"] ?? "-",
  },
  carveSplitName: {
    label: "carve split name",
    cell: (item: any) => item["accounting"]["carveSplitName"] ?? "-",
  },
  businessUseCase: {
    label: "business usecase",
    cell: (item: any) => item["accounting"]["businessUseCase"] ?? "-",
  },
  fusenEnabled: {
    label: "fusen enabled",
    cell: (item: any) => item["fusen"]["fusenEnabled"] ?? "-",
  },
  fusenEnabledDate: {
    label: "fusen enabled date",
    cell: (item: any) => item["fusen"]["fusenEnabledDate"] ?? "-",
  },
  migrationTargetStage: {
    label: "migration target stage",
    cell: (item: any) => item["fusen"]["migrationTargetStage"] ?? "-",
  },
};

const EXTERNAL_DATA_SOURCE_COLUMNS: dataColumns = {
  fuseSubsPlanId: {
    label: "fuse subs plan id",
    cell: (item: any) =>
      item.subscriptionUpdateMessage?.current?.subscription?.planID ?? "-",
  },
  subscriptionId: "subscription id",
  operationInternalId: "operation internal id",
  entitlementId: "entitlement id",
  entitlementInternalId: "entitlement internal id",
};

const FUSEN_COLUMNS: dataColumns = {
  eventVersionOverride: "event version override",
  sourceS3Key: "s3 key",
  sourceS3Bucket: "s3 bucket",
  fusenMessageGenerated: "fusen message generated",
  fusenMessageArchived: "fusen message archived",
  fusenClientEventGenerated: "fusen client event generated",
};

const DUMMY_COLUMN_DEFINITION = {
  id: "",
  header: "",
  cell: () => "",
};

export class PayloadDisplay extends React.Component<
  PayloadProps,
  { searchId: string }
> {
  constructor(props: any) {
    super(props);
    this.state = {
      searchId: this.props.searchId,
    };
  }

  render() {
    const { searchResults, searchId } = this.props;
    const Domain = configureDomain;
    const copyMessage = (val: string) => {
      navigator.clipboard.writeText(val);
    };
    const externalIdLink = ((domain) => {
      switch (domain) {
        case "prod-FE":
          return "https://prod-jp.bart-portal.fuse.amazon.dev/transactionsearch/external_id/";
        case "prod-US":
          return "https://prod-us.bart-portal.fuse.amazon.dev/transactionsearch/external_id/";
        case "prod-EU":
          return "https://prod-eu.bart-portal.fuse.amazon.dev/transactionsearch/external_id/";
      }
      return "https://beta.bart-portal.fuse.amazon.dev/transactionsearch/external_id/";
    })(Domain);
    const subscriptionsLink = ((domain) => {
      switch (domain) {
        case "prod-FE":
          return "https://subscriptions-fe.amazon.com/#/subscription/";
        case "prod-US":
          return "https://subscriptions-na.amazon.com/#/subscription/";
        case "prod-EU":
          return "https://subscriptions-eu.amazon.com/#/subscription/";
      }
      return "https://subscriptions-na.integ.amazon.com/#/subscription/";
    })(Domain);
    const flashUILink = ((domain) => {
      switch (domain) {
        case "prod-FE":
          return "https://flashui-fe.aka.amazon.com/search/transaction?transactionId=";
        case "prod-US":
          return "https://flashui-na.aka.amazon.com/search/transaction?transactionId=";
        case "prod-EU":
          return "https://flashui-eu.aka.amazon.com/search/transaction?transactionId=";
      }
      return "https://flashui-beta-na.integ.amazon.com/search/transaction?transactionId=";
    })(Domain);
    const rACEUILink = ((domain) => {
      switch (domain) {
        case "prod-FE":
          return "https://race-website-fe.aka.amazon.com/deferral_search/index?deferral-id=";
        case "prod-US":
          return "https://race-website-na.aka.amazon.com/deferral_search/index?deferral-id=";
        case "prod-EU":
          return "https://race-website-eu.aka.amazon.com/deferral_search/index?deferral-id=";
      }
      return "https://race-website.integ.amazon.com:13743/deferral_search/index?deferral-id=";
    })(Domain);
    const fusePlanIdLink = ((domain) => {
      switch (domain) {
        case "prod-FE":
          return "https://prod-jp.bart-portal.fuse.amazon.dev/getplan/";
        case "prod-US":
          return "https://prod-us.bart-portal.fuse.amazon.dev/getplan/";
        case "prod-EU":
          return "https://prod-eu.bart-portal.fuse.amazon.dev/getplan/";
      }
      return "https://beta.bart-portal.fuse.amazon.dev/getplan/";
    })(Domain);
    const fusePlanIdFenderLink = ((domain) => {
      switch (domain) {
        case "prod-FE":
          return "https://fender.corp.amazon.com/domains/prod-FE/plans/";
        case "prod-US":
          return "https://fender.corp.amazon.com/domains/prod-NA/plans/";
        case "prod-EU":
          return "https://fender.corp.amazon.com/domains/prod-EU/plans/";
      }
      return "https://fender.corp.amazon.com/domains/devo-FE/plans/";
    })(Domain);
    if (searchResults != "init") {
      if (searchResults == "error") {
        return (
          <div>
            <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
              <Alert header="Search timed out!" type="error"></Alert>
            </Grid>
          </div>
        );
      }
      if (
        searchResults == "invalid" ||
        !searchResults ||
        !searchResults.fbtsV2Response
      ) {
        return (
          <div>
            <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
              <Alert header="No results found!" type="error"></Alert>
            </Grid>
          </div>
        );
      }
      const { fbtsV2Response, ftsResponse } = searchResults;

      const balanceDataColumnDefinitions: any[] = [
        DUMMY_COLUMN_DEFINITION,
        ...Object.entries(BALANCE_DATA_COLUMNS).map(([column, value]) => ({
          id: column,
          header: typeof value === "string" ? value : value.label,
          cell: (item: any) =>
            typeof value === "string"
              ? item[column] !== undefined
                ? item[column].toString()
                : "-"
              : value.cell(item),
          sortingField: column,
        })),
        DUMMY_COLUMN_DEFINITION,
      ];
      const balanceDataTable = (
        <Table
          columnDefinitions={balanceDataColumnDefinitions}
          items={fbtsV2Response["balanceItems"].toSorted(
            (item1: any, item2: any) =>
              item1["lineNumber"] - item2["lineNumber"],
          )}
          wrapLines={false}
          resizableColumns={false}
          header={
            <Header>
              Balance Items for Transaction{" "}
              <i>{fbtsV2Response["transactionId"]}</i>
            </Header>
          }
        ></Table>
      );

      // control parameters (accounting + fusen)
      const controlParameterColumnDefinitions: any[] = [
        DUMMY_COLUMN_DEFINITION,
        ...Object.entries(CONTROL_PARAMETER_COLUMNS).map(([column, value]) => ({
          id: column,
          header: typeof value === "string" ? value : value.label,
          cell: (item: any) =>
            typeof value === "string"
              ? item[column] !== undefined
                ? item[column].toString()
                : "-"
              : value.cell(item),
          sortingField: column,
        })),
        DUMMY_COLUMN_DEFINITION,
      ];
      const controlParameterTable = (
        <Table
          columnDefinitions={controlParameterColumnDefinitions}
          items={[fbtsV2Response["controlParameters"]]}
          wrapLines={false}
          resizableColumns={false}
          header={
            <Header>
              Control Parameters for Transaction{" "}
              <i>{fbtsV2Response["transactionId"]}</i>
            </Header>
          }
        ></Table>
      );

      // external data source
      const externalDataSource = JSON.parse(
        fbtsV2Response["externalDataSource"],
      );
      const externalDataSourceBlob = (
        <Collapsible
          open={false}
          title={
            "Full External Data Source for Transaction " +
            fbtsV2Response["transactionId"]
          }
        >
          <pre className="sourceInfo ">
            {JSON.stringify(externalDataSource, null, 2)}
          </pre>
        </Collapsible>
      );

      const externalDataSourceColumns: any[] = [
        DUMMY_COLUMN_DEFINITION,
        {
          id: "FusePlanId",
          header: "fuse plan id",
          cell: (item: any) =>
            item["fusePlanId"] ? (
              <div>
                <a
                  href={fusePlanIdFenderLink + item["fusePlanId"]}
                  target="_blank"
                >
                  {item["fusePlanId"]}
                </a>
                <a href={fusePlanIdLink + item["fusePlanId"]} target="_blank">
                  <Button>BART</Button>
                </a>
              </div>
            ) : (
              "-"
            ),
        },
        ...Object.entries(EXTERNAL_DATA_SOURCE_COLUMNS).map(
          ([column, value]) => ({
            id: column,
            header: typeof value === "string" ? value : value.label,
            cell: (item: any) =>
              typeof value === "string"
                ? item[column] !== undefined
                  ? item[column].toString()
                  : "-"
                : value.cell(item),
            sortingField: column,
          }),
        ),
        DUMMY_COLUMN_DEFINITION,
      ];
      const externalDataSourceTable = (
        <Table
          columnDefinitions={externalDataSourceColumns}
          items={[externalDataSource]}
          wrapLines={false}
          resizableColumns={false}
          header={
            <Header>
              Important External Data Source Fields for Transaction{" "}
              <i>{fbtsV2Response["transactionId"]}</i>
            </Header>
          }
        ></Table>
      );

      const getSubscriptionId = (item: any) =>
        item["externalTransactionId"].split("-")[0];
      const getDeferralId = (item: any) =>
        item["externalTransactionId"].split(".")[0];

      const transactionColumnDefinitions: any[] = [
        DUMMY_COLUMN_DEFINITION,
        {
          id: "external_id",
          header: "external id",
          cell: (item: any) => (
            <a
              href={externalIdLink + item["externalTransactionId"]}
              target="_blank"
            >
              {item["externalTransactionId"]}
            </a>
          ),
        },
        {
          id: "transaction_id",
          header: "transaction id",
          cell: (item: any) => (
            <div>
              {item["transactionId"]}
              <Button onClick={() => copyMessage(item["transactionId"])}>
                <img src={Logo} width={10} />
              </Button>
            </div>
          ),
        },
        ...Object.entries(TRANSACTION_COLUMNS).map(([column, value]) => {
          return {
            id: column,
            header: typeof value === "string" ? value : value.label,
            cell: (item: any) =>
              typeof value === "string"
                ? item[column] !== undefined
                  ? item[column].toString()
                  : "-"
                : value.cell(item),
            sortingField: column,
          };
        }),
        {
          id: "Subs  link",
          header: "subs link",
          cell: (item: any) => (
            <a
              href={subscriptionsLink + getSubscriptionId(item)}
              target="_blank"
            >
              {getSubscriptionId(item)}
            </a>
          ),
        },
        {
          id: "Flash  link",
          header: "flash link",
          cell: (item: any) => (
            <a href={flashUILink + getDeferralId(item)} target="_blank">
              {getDeferralId(item)}
            </a>
          ),
        },
        {
          id: "rACE  link",
          header: "rACE link",
          cell: (item: any) => (
            <a href={rACEUILink + getDeferralId(item)} target="_blank">
              {getDeferralId(item)}
            </a>
          ),
        },
        DUMMY_COLUMN_DEFINITION,
      ];

      const transactionTable = (
        <Table
          columnDefinitions={transactionColumnDefinitions}
          items={[fbtsV2Response]}
          wrapLines={false}
          resizableColumns={false}
        ></Table>
      );

      let fusenTable;

      if (ftsResponse && ftsResponse.result && ftsResponse.result.length > 0) {
        const fusenColumnDefinitions = [
          DUMMY_COLUMN_DEFINITION,
          ...Object.entries(FUSEN_COLUMNS).map(([column, value]) => ({
            id: column,
            header: typeof value === "string" ? value : value.label,
            cell: (item: any) =>
              typeof value === "string"
                ? item[column] !== undefined
                  ? item[column].toString()
                  : "-"
                : value.cell(item),
            sortingField: column,
          })),
          DUMMY_COLUMN_DEFINITION,
        ];

        fusenTable = (
          <Table
            columnDefinitions={fusenColumnDefinitions}
            items={ftsResponse.result}
            wrapLines={false}
            resizableColumns={false}
            header={<Header>FUSEN Tracking Details</Header>}
          />
        );
      }

      return (
        <div style={{ paddingLeft: "10px" }}>
          <SpaceBetween size="l">
            {transactionTable}
            {fusenTable}
            {balanceDataTable}
            {controlParameterTable}
            {externalDataSourceTable}
            {externalDataSourceBlob}
          </SpaceBetween>
        </div>
      );
    } else {
      return <Spinner size="large"></Spinner>;
    }
  }
}

export default PayloadDisplay;
